import React, { useEffect, useState } from "react";
import SideNavLayout from "../customsidenav/sidenavlayout";


const CCMScreen = () => {
  const [formData, setFormData] = useState();

  const addField = (key, placeholder) => {
    setFormData(prev => ({
      ...prev,
      [key]: [...(prev[key] || []), ""],
    }));
  };

  const updateField = (key, index, value) => {
    const updated = [...formData[key]];
    updated[index] = value;
    setFormData(prev => ({ ...prev, [key]: updated }));
  };

  const removeField = (key, index) => {
    const updated = [...formData[key]];
    updated.splice(index, 1);
    setFormData(prev => ({ ...prev, [key]: updated }));
  };

  const toggleCheckbox = (key, subKey) => {
    setFormData(prev => {
      if (subKey) {
        return { ...prev, [key]: { ...prev[key], [subKey]: !prev[key][subKey] } };
      }
      return { ...prev, [key]: !prev[key] };
    });
  };

  // Utility to render dynamic lists
  const renderList = (key, placeholder) => (
    <div className="space-y-2">
      {(formData[key] || []).map((item, idx) => (
        <div key={idx} className="flex items-center space-x-2">
          <span className="text-blue-700 font-bold">•</span>
          <input
            type="text"
            className="flex-1 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={placeholder}
            value={item}
            onChange={e => updateField(key, idx, e.target.value)}
          />
          <button
            className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
            onClick={() => removeField(key, idx)}
          >
            Remove
          </button>
        </div>
      ))}
      <button
        className="bg-green-500 text-white px-3 py-1 rounded mt-2 hover:bg-green-600"
        onClick={() => addField(key, placeholder)}
      >
        + Add
      </button>
    </div>
  );

  return (
    <SideNavLayout>
      <div className="max-w-5xl mx-auto p-6 text-start bg-white rounded shadow">
        <button className="bg-blue-700 text-white px-4 py-2 rounded mb-4 hover:bg-blue-800" onClick={() => window.print()}>
          Print Care Plan
        </button>

        <div className="text-center mb-6 border-b-4 border-blue-700 pb-2">
          <h1 className="text-2xl font-bold text-blue-700">PATIENT-CENTERED CARE PLAN</h1>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6 bg-gray-100 p-4 rounded">
          <div>
            <label className="font-bold text-gray-700">Patient name:</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded px-2 py-1 mt-1"
              value={formData.patientName}
              onChange={e => setFormData(prev => ({ ...prev, patientName: e.target.value }))}
            />
          </div>
          <div>
            <label className="font-bold text-gray-700">Date:</label>
            <input
              type="date"
              className="w-full border border-gray-300 rounded px-2 py-1 mt-1"
              value={formData.date}
              onChange={e => setFormData(prev => ({ ...prev, date: e.target.value }))}
            />
          </div>
        </div>

        <div className="mb-6">
          <label className="font-bold text-gray-700">Provider name:</label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded px-2 py-1 mt-1"
            value={formData.providerName}
            onChange={e => setFormData(prev => ({ ...prev, providerName: e.target.value }))}
          />
        </div>

        <p className="font-bold text-green-600 mb-4">Complete the next four sections prior to your visit:</p>

        <div className="mb-6 border-l-4 border-green-500 pl-4 space-y-4">
          <div>
            <h2 className="font-bold text-blue-700 mb-2">Top concerns and barriers</h2>
            <div>
              <p className="font-semibold">The main things I would like to fix or improve about my health are:</p>
              {renderList("healthConcerns", "Enter main health concern")}
            </div>
            <div className="mt-4">
              <p className="font-semibold">The main things preventing me from improving my health are:</p>
              {renderList("healthBarriers", "Enter barrier to health improvement")}
            </div>
          </div>

          <div>
            <h2 className="font-bold text-blue-700 mb-2">Symptom management</h2>
            <p className="font-semibold mb-2">The main symptoms I wish to reduce or eliminate are:</p>
            {renderList("symptoms", "Enter symptom")}
            <p className="text-sm italic mt-2">
              To treat these, your provider will help you complete the "Summary of things I need to do," next page, at your appointment.
            </p>
          </div>

          <div>
            <h2 className="font-bold text-blue-700 mb-2">Health care providers</h2>
            {renderList("otherProviders", "Enter provider type and name")}
          </div>

          <div>
            <h2 className="font-bold text-blue-700 mb-2">Resources and supports</h2>
            {renderList("supportResources", "Enter support person or resource")}
          </div>
        </div>

        <p className="font-bold text-orange-500 mb-4">Complete the remaining sections with your provider at your appointment:</p>

        <div className="mb-6 border-l-4 border-orange-500 pl-4 space-y-4">
          <div>
            <h2 className="font-bold text-blue-700 mb-2">My medications*</h2>
            {renderList("medications", "Enter medication name, dosage, frequency")}
            <div className="mt-2 space-y-1">
              {Object.entries(formData.medicationAgreements).map(([key, value]) => (
                <label key={key} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={() => toggleCheckbox("medicationAgreements", key)}
                  />
                  <span className="text-sm">{key.replace(/([A-Z])/g, " $1")}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-bold text-blue-700 mb-2">My allergies*</h2>
            {renderList("allergies", "Enter allergy")}
          </div>

          <div>
            <h2 className="font-bold text-blue-700 mb-2">My conditions*</h2>
            {renderList("conditions", "Enter medical condition")}
            <label className="flex items-center mt-2 space-x-2">
              <input
                type="checkbox"
                checked={formData.reviewedConditions}
                onChange={() => toggleCheckbox("reviewedConditions")}
              />
              <span>I have reviewed my list of conditions</span>
            </label>
          </div>

          <div>
            <h2 className="font-bold text-blue-700 mb-2">Treatment goals/targets</h2>
            {renderList("treatmentGoals", "Enter specific, measurable goal")}
          </div>

          <div>
            <h2 className="font-bold text-blue-700 mb-2">Summary of things I need to do</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="font-semibold">Tests to complete</p>
                {renderList("summary.tests", "Enter test and timeframe")}
              </div>
              <div>
                <p className="font-semibold">Other health professionals to see</p>
                {renderList("summary.professionals", "Enter professional and timeframe")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </SideNavLayout>
  );
};

export default CCMScreen;
